import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { AWS_CONFIG } from '../config/aws';
import { toYoutubeEmbed, youtubeThumb } from '../utils/youtube';
import YoutubeChannelManager from './YoutubeChannelManager';

const CATEGORY = 'youtube';

// Admin "유튜브 관리" tab — manage an unlimited list of YouTube videos shown on the
// home intro section. Reuses the /images API (category 'youtube'); the URL lives
// in the imageUrl field, the caption in alt. No new backend endpoint.
export default function VideoManager() {
  const { getIdToken } = useAuth();
  const [videos, setVideos] = useState([]);
  const [draft, setDraft] = useState({ url: '', caption: '' });
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState('');

  const fetchVideos = useCallback(async () => {
    try {
      const res = await fetch(`${AWS_CONFIG.apiUrl}/images?category=${CATEGORY}`);
      const data = await res.json();
      setVideos(data.images || []);
    } catch {
      setError('영상 목록을 불러오지 못했습니다.');
    }
  }, []);

  useEffect(() => { fetchVideos(); }, [fetchVideos]);

  async function handleAdd() {
    const url = draft.url.trim();
    if (!toYoutubeEmbed(url)) {
      setError('올바른 유튜브 링크가 아닙니다 (예: https://youtu.be/abcd...)');
      return;
    }
    setError(''); setBusy('add');
    try {
      const token = await getIdToken();
      if (!token) throw new Error('로그인 세션이 만료되었습니다.');
      const nextSort = videos.length > 0 ? Math.max(...videos.map(v => v.sortOrder || 0)) + 1 : 1;
      const res = await fetch(`${AWS_CONFIG.apiUrl}/images/${CATEGORY}/${nextSort}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: token },
        body: JSON.stringify({ imageUrl: url, alt: draft.caption.trim() || `영상 ${nextSort}` }),
      });
      if (!res.ok) throw new Error(`저장 실패 ${res.status}`);
      setDraft({ url: '', caption: '' });
      await fetchVideos();
    } catch (err) {
      setError(`추가 실패: ${err.message}`);
    }
    setBusy(null);
  }

  async function handleDelete(v) {
    setBusy(`del-${v.sortOrder}`);
    try {
      const token = await getIdToken();
      const res = await fetch(`${AWS_CONFIG.apiUrl}/images/${CATEGORY}/${v.sortOrder}`, {
        method: 'DELETE', headers: { Authorization: token },
      });
      if (!res.ok) throw new Error(`삭제 실패 ${res.status}`);
      await fetchVideos();
    } catch (err) {
      setError(`삭제 실패: ${err.message}`);
    }
    setBusy(null);
  }

  return (
    <div>
      <h1>유튜브 관리</h1>
      <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginTop: 0, marginBottom: 14 }}>
        유튜브 링크를 <strong style={{ color: 'var(--text)' }}>무제한으로</strong> 등록할 수 있어요.
        홈 화면 '소개' 자리에 영상이 표시되며, 여러 개면 썸네일로 전환됩니다. 등록이 없으면 '준비 중'으로 표시됩니다.
      </p>
      <YoutubeChannelManager />

      {error && (
        <div className="card" style={{
          padding: 12, marginBottom: 16, borderColor: '#ff5252', background: 'rgba(255,82,82,0.08)',
          color: '#ff5252', fontSize: '0.9rem',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12,
        }}>
          <span>{error}</span>
          <button onClick={() => setError('')} aria-label="닫기" style={{
            background: 'none', border: 'none', color: '#ff5252', fontSize: '1.2rem', lineHeight: 1, cursor: 'pointer',
          }}>×</button>
        </div>
      )}

      <div className="card" style={{ padding: 16, marginBottom: 16 }}>
        <input
          className="input"
          placeholder="유튜브 링크 (https://youtu.be/... 또는 watch?v=...)"
          value={draft.url}
          onChange={(e) => setDraft(d => ({ ...d, url: e.target.value }))}
          style={{ width: '100%', marginBottom: 10 }}
        />
        <input
          className="input"
          placeholder="설명 (선택, 예: 외부 스팀세차 과정)"
          value={draft.caption}
          onChange={(e) => setDraft(d => ({ ...d, caption: e.target.value }))}
          style={{ width: '100%', marginBottom: 12 }}
        />
        <button className="btn btn-primary" onClick={handleAdd} disabled={busy === 'add' || !draft.url.trim()} style={{ padding: '10px 18px' }}>
          {busy === 'add' ? '추가 중...' : '+ 영상 추가 (계속 추가 가능)'}
        </button>
      </div>

      <h2 style={{ fontSize: '1.1rem', marginBottom: 12, color: 'var(--text-muted)' }}>
        등록된 영상 ({videos.length}개 · 무제한)
      </h2>
      <div className="image-manager-grid">
        {videos.map(v => (
          <div key={v.sortOrder} className="image-manager-card">
            <div className="image-manager-preview" style={{ aspectRatio: '16 / 9' }}>
              {youtubeThumb(v.imageUrl)
                ? <img src={youtubeThumb(v.imageUrl)} alt={v.alt || '영상'} />
                : <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'var(--text-dim)', fontSize: '0.8rem' }}>잘못된 링크</div>}
            </div>
            <div className="image-manager-info">
              <div className="image-manager-label">{v.alt || `영상 ${v.sortOrder}`}</div>
              <button
                className="image-manager-btn"
                disabled={busy === `del-${v.sortOrder}`}
                onClick={() => handleDelete(v)}
                style={{ color: '#ff5252', borderColor: '#ff5252' }}
              >
                {busy === `del-${v.sortOrder}` ? '삭제 중...' : '삭제'}
              </button>
            </div>
          </div>
        ))}
        {videos.length === 0 && (
          <p style={{ color: 'var(--text-dim)', fontSize: '0.85rem', padding: '12px 0' }}>
            아직 등록된 영상이 없습니다. 위에서 첫 영상을 추가해주세요.
          </p>
        )}
      </div>
    </div>
  );
}
